import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  Nav,
  NavItem,
  NavLink,
  UncontrolledPopover,
  PopoverHeader,
  PopoverBody,
  Label,
} from "reactstrap";
import Select from "react-select";
import AdminHeader from "components/Headers/AdminHeader";
import { DEFAULT_RECORDS_PER_PAGE } from "components/constant";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import { useDispatch, useSelector } from "react-redux";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { useLocation, useNavigate } from "react-router-dom";
import { downloadLetter } from "action/letter";
import DatePicker from "react-datepicker";
import { getSALettersList } from "action/superAdmin";
import { lettersList } from "constants/superAdmin";
import {
  LETTER_TYPE_OPTIONS,
  LETTER_SCOPE_OPTIONS,
} from "components/constant";
import { showAlert } from "components/CommonComponents/CommonFunctions";
import { letterRequestApproval } from "action/superAdmin";

function SuperAdminLetterList() {
  const location = useLocation();
  const { notify } = useContext(NotificationContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const letterDataList = useSelector((state) => state.superAdmin.lettersList);

  const [pageNum, setPageNum] = useState(1);
  const [filterState, setFilterState] = useState({
    letterType: "",
    documentScope: "",
    referenceNo: "",
    issuedToCustomer: "",
    project: "",
    voltage: "",
    agency: "",
    issueDate: "",
    validTill: "",
    status: "PENDING_APPROVAL",
    filterParams: [],
  });

  const [alternative, setAlternative] = useState({
    isLoading: true,
    showLoaderOnClick: false,
    error: "",
    noRecords: false,
  });

  const [isDownloading, setIsDownloading] = React.useState(false);
  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    fileUrl: "",
  });
  const [activeTab, setActiveTab] = useState("pending-approval");
  const [showLoaderOnClick, setShowLoaderOnClick] = useState({ approve: false, reject: false });

  const STATUS_MAP = {
    "pending-approval": "PENDING_APPROVAL",
    rejected: "REJECTED",
    approved: "APPROVED",
  };

  const buildFilterParams = () => {
    const filters = [];

    if (filterState.documentType) {
      filters.push({
        field: "LetterType",
        condition: "eq",
        filterValues: [filterState.documentType.value],
      });
    }

    if (filterState.documentScope) {
      filters.push({
        field: "LetterScope",
        condition: "eq",
        filterValues: [filterState.documentScope.value],
      });
    }

    if (filterState.referenceNo?.trim()) {
      filters.push({
        field: "ReferenceNo",
        condition: "like",
        filterValues: [filterState.referenceNo?.trim()],
      });
    }

    if (filterState.issuedToCustomer?.trim()) {
      filters.push({
        field: "IssuedToCustomer",
        condition: "like",
        filterValues: [filterState.issuedToCustomer?.trim()],
      });
    }

    if (filterState.project?.trim()) {
      filters.push({
        field: "Project",
        condition: "like",
        filterValues: [filterState.project?.trim()],
      });
    }

    if (filterState.requestOf?.trim()) {
      filters.push({
        field: "RequestOf",
        condition: "like",
        filterValues: [filterState.requestOf?.trim()],
      });
    }

    if (filterState.agency?.trim()) {
      filters.push({
        field: "Agency",
        condition: "like",
        filterValues: [filterState.agency.trim()],
      });
    }

    if (filterState.issueDate[0]) {
      if (filterState.issueDate[1]) {
        // Range selected
        filters.push({
          field: "IssueDate",
          condition: "btw",
          filterValues: [filterState.issueDate[0], filterState.issueDate[1]],
        });
      } else {
        // Single date selected
        filters.push({
          field: "IssueDate",
          condition: "eq",
          filterValues: [filterState.issueDate[0]],
        });
      }
    }

    if (filterState.validTill[0]) {
      if (filterState.validTill[1]) {
        // Range selected
        filters.push({
          field: "ValidTill",
          condition: "btw",
          filterValues: [filterState.validTill[0], filterState.validTill[1]],
        });
      } else {
        // Single date selected
        filters.push({
          field: "ValidTill",
          condition: "eq",
          filterValues: [filterState.validTill[0]],
        });
      }
    }

    if (filterState.status) {
      filters.push({
        field: "Status",
        condition: "eq",
        filterValues: [filterState.status],
      });
    }

    return {
      filters: filters.length > 0 ? JSON.stringify(filters) : [],
    };
  };

  //trainee List added sr no. for each item
  const formattedTraineeList = letterDataList?.data?.Letter?.map(
    (item, index) => {
      return {
        ...item,
        srNo:
          (pageNum - 1) *
          (letterDataList?.data?.noOfRecordsPerPage ||
            DEFAULT_RECORDS_PER_PAGE) +
          index +
          1,
      };
    },
  );

  useEffect(() => {
    getSALettersList(
      dispatch,
      pageNum,
      buildFilterParams(),
      filterState.searchName,
    );
    return () => {
      dispatch({ type: lettersList, payload: {} });
    };
  }, [dispatch, pageNum, filterState.filterParams]);

  useEffect(() => {
    if (letterDataList?.code === 200) {
      setAlternative({
        isLoading: false,
        error: "",
        noRecords: letterDataList?.data?.Letter?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (letterDataList?.code || letterDataList?.message) {
      setAlternative({
        isLoading: false,
        error:
          letterDataList?.message ||
          "Failed to load letters list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [letterDataList]);

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      const params = new URLSearchParams(location.search);
      params.set("tab", tab);
      navigate(`${location.pathname}?${params.toString()}`, {
        replace: true,
      });
      const newStatus = STATUS_MAP[tab];

      const newFilterParams = {
        filters: JSON.stringify([
          {
            field: "Status",
            condition: "eq",
            filterValues: [newStatus],
          },
        ]),
      };

      setFilterState((prev) => ({
        ...prev,
        status: newStatus,
        filterParams: newFilterParams,
      }));

      if (pageNum !== 1) setPageNum(1);
    }
  };

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
    },
    {
      Header: "Letter Type",
      accessor: "letterType",
      Cell: ({ value }) => <span>{value || "-"}</span>,
    },
    {
      Header: "Letter scope",
      accessor: "letterScope",
      Cell: ({ value }) => <span>{value || "-"}</span>,
    },
    {
      Header: "Reference No",
      accessor: "referenceNo",
      Cell: ({ value }) => <span>{value || "-"}</span>,
    },
    {
      Header: "Project",
      accessor: "project",
      minWidth: 280,
      maxWidth: 350,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Issued To Customer",
      accessor: "issuedToCustomer",
      minWidth: 220,
      maxWidth: 350,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Other Information",
      accessor: "otherInfo",
      disableSortBy: true,
      Cell: ({ row }) => {
        const data = row.original;
        const id = data.ID;

        return (
          <>
            {/* Styled Button */}
            <Button
              size="sm"
              className="btn-link d-flex align-items-center p-0"
              id={`otherInfo_${id}`}
            >
              <i className="fa fa-info-circle mr-1" />
              Details
            </Button>

            <UncontrolledPopover
              placement="right"
              target={`otherInfo_${id}`}
              trigger="hover focus"
            >
              <PopoverHeader className="text-center">
                Letter Details
              </PopoverHeader>

              <PopoverBody className="custom-popover-body">
                {data?.project && <><div className="mb-2">
                  <small className="text-muted">Project</small>
                  <div className="font-weight-bold">{data.project || "-"}</div>
                </div>

                  <hr className="my-2" /></>}

                <div className="d-flex mb-2">
                  <div className="w-50">
                    <small className="text-muted">Request Of</small>
                    <div className="font-weight-bold">
                      {data.requestOf || "-"}
                    </div>
                  </div>

                  <div className="w-50">
                    <small className="text-muted">Created At</small>
                    <div className="font-weight-bold">
                      {data.createdAt || "-"}
                    </div>
                  </div>
                </div>

                <hr className="my-2" />

                <div className="d-flex mb-2">
                  <div className="w-50">
                    <small className="text-muted">Issued On</small>
                    <div className="font-weight-bold">
                      {data.issueDate || "-"}
                    </div>
                  </div>

                  <div className="w-50">
                    <small className="text-muted">Valid Till</small>
                    <div className="font-weight-bold">
                      {data.validTill || "-"}
                    </div>
                  </div>
                </div>

                <hr className="my-2" />

                <div className="d-flex mb-2">
                  <div className="w-50">
                    <small className="text-muted">Jointer Name</small>

                    {data.LetterUsers?.length ? (
                      <div className="mt-1">
                        {data.LetterUsers.map((user, index) => (
                          <div key={index} className="font-weight-bold">
                            {index + 1}. {user.name}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-muted mt-1">-</div>
                    )}
                  </div>

                  <div className="w-50">
                    <small className="text-muted">Government ID</small>

                    {data.LetterUsers?.length ? (
                      <div className="mt-1">
                        {data.LetterUsers.map((user, index) => (
                          <div key={index} className="font-weight-bold">
                            {user.governmentID || "-"}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-muted mt-1">-</div>
                    )}
                  </div>
                </div>

                <hr className="my-2" />

                <div className="d-flex mb-2">
                  <div className="w-50">
                    <small className="text-muted">Created By</small>
                    <div className="font-weight-bold">
                      {data.createdByName || "-"}
                    </div>
                  </div>

                  {data.approvedBy > 0 && <div className="w-50">
                    <small className="text-muted">{data.status === "APPROVED" ? "Approved By" : data.status === "REJECTED" ? "Rejected By" : "Action By"}</small>
                    <div className="font-weight-bold">
                      {data.approvedByName || "-"}
                    </div>
                  </div>}
                </div>
              </PopoverBody>
            </UncontrolledPopover>
          </>
        );
      },
    },
    {
      Header: "View Letter",
      accessor: "referenceNoPath",
      disableSortBy: true,
      Cell: ({ value, row }) => {
        const id = row.original.ID;

        return value ? (
          <div className="actions-right d-flex justify-content-start">
            <Button
              className="btn-link d-flex align-items-center p-0"
              id={`preview${id}`}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handlePreviewLetter(row.original.referenceNo);
              }}
            >
              <i className="fa fa-eye mr-1"></i>
              <span>Preview</span>
            </Button>
          </div>
        ) : (
          "-"
        );
      },
    },
  ];

  if (activeTab === "pending-approval") {
    columns.push({
      Header: "Created By",
      accessor: "createdByName",
      minWidth: 180,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    });
    columns.push({
      Header: "Actions",
      accessor: "actions",
      disableSortBy: true,
      Cell: ({ row }) => {
        return (
          <div className="d-flex gap-2">
            <Button
              size="sm"
              color="success"
              outline
              onClick={() => handleApprove(row.original.ID)}
            >
              {showLoaderOnClick.approve ? (
                <i className="fa fa-spinner fa-spin" />
              ) : (<>
                <i className="fa fa-check mr-1" />Approve</>)}
            </Button>

            <Button
              size="sm"
              color="danger"
              outline
              onClick={() => handleReject(row.original.ID)}
            >
              {showLoaderOnClick.reject ? (
                <i className="fa fa-spinner fa-spin" />
              ) : (<>
                <i className="fa fa-times mr-1" />Reject
              </>)}
            </Button>
          </div>
        );
      },
    });
  }

  if (activeTab === "rejected") {
    columns.push({
      Header: "Rejection Reason",
      accessor: "rejectionReason",
      disableSortBy: true,
      minWidth: 220,
      maxWidth: 350,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    })
    columns.push({
      Header: "Rejected By",
      accessor: "approvedByName",
      minWidth: 180,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    });
  }

  if (activeTab === "approved") {
    columns.push({
      Header: "Approved By",
      accessor: "approvedByName",
      minWidth: 180,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    });
  }

  const handleApprove = (letterID) => {
    showAlert({
      title: "Are you sure you want to approve this letter?",
      message: "This action is final. The letter cannot be edited after approval.",
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          setShowLoaderOnClick((prev) => ({ ...prev, approve: true }));

          const res = await letterRequestApproval(letterID, { status: "APPROVED" });

          if (res?.status === 200 || res?.code === 200) {
            notify(
              "success",
              res?.message || "Letter approved successfully.",
            );
            getSALettersList(
              dispatch,
              pageNum,
              buildFilterParams(),
              filterState.searchName,
            );
          } else {
            const message =
              res?.message || "Failed to approve letter. Please try again.";
            notify("danger", message);
          }
        } catch (error) {
          notify("danger", "Failed to approve letter.");
        } finally {
          setShowLoaderOnClick((prev) => ({ ...prev, approve: false }));
        }
      },
    });
  };

  const handleReject = (letterID) => {
    let reason = ""; // local variable scoped to the modal

    showAlert({
      title: "Are you sure you want to reject this letter?",
      message: "After rejection, the letter will be sent back for required changes",
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Yes, Reject",
      cancelText: "Cancel",
      children: (
        <textarea
          className="form-control mt-2"
          rows={3}
          placeholder="Enter rejection reason"
          onChange={(e) => (reason = e.target.value)} // update local variable
        />
      ),
      onConfirm: async () => {
        if (!reason?.trim()) {
          notify("danger", "Rejection reason is required.");
          return false; // keep modal open
        }

        try {
          setShowLoaderOnClick((prev) => ({ ...prev, reject: true }));

          const res = await letterRequestApproval(letterID, {
            status: "REJECTED",
            RejectionReason: reason.trim(),
          });

          if (res?.status === 200 || res?.code === 200) {
            notify("success", res?.message || "Letter rejected successfully.");
            getSALettersList(dispatch, pageNum, buildFilterParams(), filterState.searchName);
          } else {
            notify("danger", res?.message || "Failed to reject letter. Please try again.");
          }
        } catch (error) {
          notify("danger", "Failed to reject letter.");
        } finally {
          setShowLoaderOnClick((prev) => ({ ...prev, reject: false }));
        }
      },
    });
  };

  const handlePreviewLetter = async (ID) => {
    setIsDownloading(true);
    try {
      const res = await downloadLetter(ID);
      if (res.status === 200 && res.data) {
        const fileURL = URL.createObjectURL(res?.data);

        setPreviewModal({ isOpen: true, fileUrl: fileURL });
      } else if (res.code === 404) {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message ||
          "Failed to download authorization letter. Please try again.";
        notify("danger", message);
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message ||
          "Failed to download authorization letter. Please try again.";
        notify("danger", message);
      }
    } catch (error) {
      notify(
        "danger",
        "Failed to download authorization letter. Please try again.",
      );
    }
    setIsDownloading(false);
  };

  const clearSearch = () => {
    setFilterState((prev) => ({
      ...prev,
      documentType: "",
      documentScope: "",
      referenceNo: "",
      issuedToCustomer: "",
      project: "",
      voltage: "",
      agency: "",
      issueDate: "",
      validTill: "",
      filterParams: [],
    }))

    if (pageNum !== 1) setPageNum(1);
  };

  const handleSearch = () => {
    const newFilters = buildFilterParams();

    if (!newFilters.filters.length) return;

    if (
      JSON.stringify(filterState.filterParams) !== JSON.stringify(newFilters)
    ) {
      setAlternative((prev) => ({
        ...prev,
        showLoaderOnClick: true,
      }));

      setFilterState((prev) => ({
        ...prev,
        filterParams: newFilters,
      }));

      if (pageNum !== 1) setPageNum(1);
    }
  };

  return (
    <>
      <AdminHeader />
      {isDownloading && (
        <div className="logout-overlay">
          <div className="logout-card">
            <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-3" />
            <div className="font-weight-bold text-dark">
              Loading… Please wait.
            </div>
          </div>
        </div>
      )}
      <Container fluid className="mt--6">
        <Row className="mb--5">
          <Col md="12">
            <Card>
              <CardHeader className="pb-0 course-sticky-header">
                <Nav className="custom-tabs cursor">
                  <NavItem>
                    <NavLink
                      className={
                        activeTab === "pending-approval" ? "active" : ""
                      }
                      onClick={() => toggleTab("pending-approval")}
                    >
                      Pending Approval
                    </NavLink>
                  </NavItem>

                  <NavItem>
                    <NavLink
                      className={activeTab === "rejected" ? "active" : ""}
                      onClick={() => toggleTab("rejected")}
                    >
                      Rejected
                    </NavLink>
                  </NavItem>

                  <NavItem>
                    <NavLink
                      className={activeTab === "approved" ? "active" : ""}
                      onClick={() => toggleTab("approved")}
                    >
                      Approved
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardHeader>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col md="12">
            <Card className="mt-3">

              {/* Body */}
              <CardBody className="mt-2">
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                  {/* Left side - Filter Inputs + Search */}
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Select
                      options={LETTER_TYPE_OPTIONS}
                      styles={fixedWidthCustomStyle()}
                      className="mr-2 mb-2 filter-input-large"
                      placeholder="Letter type"
                      value={filterState.documentType}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          documentType: selected,
                        }))
                      }
                    />

                    <Select
                      options={LETTER_SCOPE_OPTIONS}
                      styles={fixedWidthCustomStyle()}
                      className="mr-2 mb-2 filter-input-large"
                      placeholder="Letter scope"
                      value={filterState.documentScope}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          documentScope: selected,
                        }))
                      }
                    />

                    <Input
                      className="mr-2 mb-2 filter-input-large"
                      placeholder="Search by Reference No"
                      value={filterState.referenceNo}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          referenceNo: e.target.value,
                        }))
                      }
                    />

                    <Input
                      className="mr-2 mb-2 filter-input-large"
                      placeholder="Search by customer name"
                      value={filterState.issuedToCustomer}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          issuedToCustomer: e.target.value,
                        }))
                      }
                    />

                    <Input
                      className="mr-2 mb-2 filter-input-large"
                      placeholder="Search by project name"
                      value={filterState.project}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          project: e.target.value,
                        }))
                      }
                    />

                    {/* <Input
                      className="mr-2 mb-2 filter-input-large"
                      placeholder="Search by Requestor"
                      value={filterState.requestOf}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          requestOf: e.target.value,
                        }))
                      }
                    /> */}

                    <div className="d-flex align-items-center mr-2 mb-2 mt-2">
                      <Label className="mb-0 mr-2 small">Issue Date</Label>
                      <DatePicker
                        selected={filterState.issueDate[0]}
                        onChange={(dates) =>
                          setFilterState((prev) => ({
                            ...prev,
                            issueDate: dates,
                          }))
                        }
                        startDate={filterState.issueDate[0]}
                        endDate={filterState.issueDate[1]}
                        selectsRange
                        isClearable={!!filterState.issueDate[0]}
                        placeholderText="Select issue date or range"
                        className="form-control mr-2 mb-2 filter-input-large"
                        dateFormat="yyyy-MM-dd"
                      />
                    </div>

                    <div className="d-flex align-items-center mr-2 mb-2 mt-2">
                      <Label className="mb-0 mr-2 small">Valid Till</Label>
                      <DatePicker
                        selected={filterState.validTill[0]}
                        onChange={(dates) =>
                          setFilterState((prev) => ({
                            ...prev,
                            validTill: dates,
                          }))
                        }
                        startDate={filterState.validTill[0]}
                        endDate={filterState.validTill[1]}
                        selectsRange
                        isClearable={!!filterState.validTill[0]}
                        placeholderText="Select valid till date or range"
                        className="form-control mr-2 mb-2 filter-input-large"
                        dateFormat="yyyy-MM-dd"
                      />
                    </div>

                    <Button
                      color="default"
                      className="d-flex align-items-center mb-2 filter-btn btn-deep-blue"
                      onClick={handleSearch}
                    >
                      {alternative.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>

                    {filterState.filterParams?.filters &&
                      JSON.parse(filterState.filterParams.filters || "[]").some(
                        (f) => f.field !== "Status"
                      ) && (
                        <Button
                          color="default"
                          className="d-flex align-items-center mb-2 ml-0 filter-btn btn-deep-blue px-4"
                          onClick={clearSearch}
                        >
                          <span> <i className="fa fa-times mr-1" />  Clear</span>
                        </Button>
                      )}
                  </div>
                </div>

                {/* <div className="mt-2 mb-3 mx-1">
                  {letterDataList?.data?.totalRecords ? (
                    <>
                      {filterState.filterParams?.filters?.length > 0 ? (
                        <span className="font-weight-bold">
                          Showing {letterDataList?.data?.totalFilteredRecords}{" "}
                          of {letterDataList?.data?.totalRecords} Records
                        </span>
                      ) : (
                        <span className="total-records">
                          Total Records : {letterDataList?.data?.totalRecords}
                        </span>
                      )}
                    </>
                  ) : null}
                </div> */}

                {alternative.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : alternative.error ? (
                  <>
                    <div className="loaderContainer">{alternative.error}</div>
                  </>
                ) : alternative.noRecords ? (
                  <div className="loaderContainer text-muted">
                    No letters found.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <CommonTable
                      columns={columns}
                      data={formattedTraineeList || []}
                      defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                      activePage={pageNum}
                      setPageNum={setPageNum}
                      totalPages={Math.ceil(
                        letterDataList?.data?.totalFilteredRecords /
                        letterDataList?.data?.noOfRecordsPerPage ||
                        DEFAULT_RECORDS_PER_PAGE,
                      )}
                      showPaginationBottom={true}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <Modal
        isOpen={previewModal.isOpen}
        toggle={() => setPreviewModal({ isOpen: false, fileUrl: "" })}
        size="xl"
      >
        <ModalHeader
          toggle={() => setPreviewModal({ isOpen: false, fileUrl: "" })}
        >
          Letter Preview
        </ModalHeader>

        <ModalBody style={{ height: "80vh" }}>
          {previewModal.fileUrl && (
            <iframe
              src={`${previewModal.fileUrl}#toolbar=0&navpanes=0&scrollbar=0`}
              title="PDF Preview"
              width="100%"
              height="100%"
              style={{ border: "none" }}
            />
          )}
        </ModalBody>
      </Modal>
    </>
  );
}

export default SuperAdminLetterList;
